import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, CheckCircle2, ShieldCheck, BatteryCharging, Smartphone, Sparkles, Clock, ArrowRight, Award, Send, Wrench, Cpu, Calculator } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { phoneRepairData } from '../mock';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';
import InstagramVideoSection from '../components/InstagramVideoSection';
import LandingFaqSchema from '../components/LandingFaqSchema';
import useLandingContent from '../hooks/useLandingContent';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MOCK_APPLE = phoneRepairData.Apple || { models: [], repairs: {} };
const DYNAMIK_LABEL = 'Заміна динаміка';
const dynamikPrice = (m) => {
  if (/15\s*pro/i.test(m)) return 2400;
  if (/\b(14|15)\b/.test(m)) return 2000;
  if (/\b(12|13)\b/.test(m)) return 1800;
  return 1500;
};

const IphoneCalculator = ({ apple }) => {
  const [model, setModel] = useState('');
  const [service, setService] = useState('');
  const services = [...Object.keys(apple.repairs || {})];
  if (!services.includes(DYNAMIK_LABEL)) services.push(DYNAMIK_LABEL);
  const raw = !model || !service
    ? undefined
    : (apple.repairs?.[service]?.[model] ?? (service === DYNAMIK_LABEL ? { original: dynamikPrice(model) } : undefined));
  const done = model && service;
  const orig = raw && typeof raw === 'object' ? raw.original : (typeof raw === 'number' ? raw : undefined);
  const copy = raw && typeof raw === 'object' ? raw.copy : undefined;
  const hasPrice = orig !== undefined || copy !== undefined;

  const tgText = encodeURIComponent(`Вітаю! Цікавить ${service || 'ремонт'} для ${model || 'iPhone'}. Підкажіть ціну та терміни.`);

  return (
    <div className="glass-card p-6 md:p-8" data-testid="iphone-calculator">
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div>
          <label className="block text-xs uppercase tracking-widest text-teal-400 mb-2">Модель iPhone</label>
          <Select value={model} onValueChange={(v) => setModel(v)}>
            <SelectTrigger className="calc-select bg-white/5 border-teal-500/30" data-testid="calc-model-trigger">
              <SelectValue placeholder="Оберіть модель" />
            </SelectTrigger>
            <SelectContent className="calc-dropdown max-h-[300px]">
              {apple.models.map((m) => (
                <SelectItem key={m} value={m} className="calc-dropdown-item">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-teal-400 mb-2">Послуга</label>
          <Select value={service} onValueChange={(v) => setService(v)}>
            <SelectTrigger className="calc-select bg-white/5 border-teal-500/30" data-testid="calc-service-trigger">
              <SelectValue placeholder="Оберіть послугу" />
            </SelectTrigger>
            <SelectContent className="calc-dropdown">
              {services.map((s) => (
                <SelectItem key={s} value={s} className="calc-dropdown-item">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {done && (
        <div className="mt-2 p-6 rounded-2xl bg-white/[0.04] border border-teal-500/30 animate-fade-in-up" data-testid="calc-result">
          {hasPrice ? (
            <div className="grid sm:grid-cols-2 gap-4 text-center">
              {orig !== undefined && (
                <div className="p-4 rounded-xl bg-white/5 border border-teal-500/20">
                  <div className="text-xs uppercase tracking-widest text-gray-400 mb-1">Оригінал</div>
                  <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-teal-400 to-cyan-500 bg-clip-text text-transparent">від {orig} грн</div>
                </div>
              )}
              {copy !== undefined && (
                <div className="p-4 rounded-xl bg-white/5 border border-teal-500/20">
                  <div className="text-xs uppercase tracking-widest text-gray-400 mb-1">Копія (аналог)</div>
                  <div className="text-3xl md:text-4xl font-black text-white">від {copy} грн</div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-300">Ціну на «{service}» для {model} уточнюйте — телефонуйте, порахуємо індивідуально.</p>
          )}
          <p className="text-gray-500 text-xs mt-4 text-center">* Орієнтовна вартість. Точну ціну називає майстер після безкоштовної діагностики.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-5">
            <a href="tel:+380974178788" data-testid="calc-cta-call" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-bold rounded-full">
              <Phone className="w-4 h-4" /> Записатись
            </a>
            <a href={`https://t.me/cimmodua?text=${tgText}`} target="_blank" rel="noopener noreferrer" data-testid="calc-cta-telegram" className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
              <Send className="w-4 h-4" /> Дізнатись у Telegram
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const ACCENT = 'from-teal-400 to-cyan-500';
const GLOW = 'shadow-[0_0_80px_rgba(45,212,191,0.4)]';
const GRAD = `bg-gradient-to-r ${ACCENT} bg-clip-text text-transparent`;

// Ціни синхронізовано з калькулятором ремонту (грн)
const GROUPS = [
  { key: 'x', label: 'iPhone X / XR / XS', models: 'X · XR · XS · XS Max', battery: 700, displayCopy: 1700, displayOriginal: 3500 },
  { key: '11', label: 'iPhone 11', models: '11 · 11 Pro · 11 Pro Max', battery: 750, displayCopy: 2500, displayOriginal: 4500 },
  { key: '12', label: 'iPhone 12', models: '12 mini · 12 · 12 Pro · 12 Pro Max', battery: 1000, displayCopy: 4500, displayOriginal: 8000 },
  { key: '13', label: 'iPhone 13', models: '13 mini · 13 · 13 Pro · 13 Pro Max', battery: 900, displayCopy: 5250, displayOriginal: 10500 },
  { key: '14', label: 'iPhone 14', models: '14 · 14 Plus · 14 Pro · 14 Pro Max', battery: 1100, displayCopy: 5750, displayOriginal: 9600 },
  { key: '15', label: 'iPhone 15', models: '15 · 15 Plus · 15 Pro · 15 Pro Max', battery: 1300, displayCopy: 8000, displayOriginal: 16000 }
];

const TRUST = [
  { icon: Cpu, title: 'Оригінальні запчастини', desc: 'Ставимо оригінальні або сертифіковані аналоги преміум-якості. Ви завжди обираєте самі.' },
  { icon: Award, title: 'Сертифіковані майстри', desc: '8+ років досвіду ремонту iPhone. Точний інструмент, антистатичне робоче місце, каліброване обладнання.' },
  { icon: ShieldCheck, title: 'Гарантія на роботи', desc: 'Офіційна гарантія: 90 днів на заміну АКБ та 30 днів на заміну дисплея.' }
];

const STEPS = [
  { title: 'Безкоштовна діагностика', desc: 'Перевіряємо стан акумулятора, дисплея та плати. Називаємо точну ціну ще до початку робіт.', img: '/images/generated/iphone-hero.png', icon: Sparkles },
  { title: 'Акуратне розкриття', desc: 'Прогріваємо корпус, знімаємо дисплей вакуумною присоскою. Ніяких сколів і подряпин.', img: '/images/generated/iphone-step-1.png', icon: Wrench },
  { title: 'Заміна компонента', desc: 'Виймаємо старий АКБ або дисплей, встановлюємо новий оригінальний компонент, оновлюємо вологозахисні прокладки.', img: '/images/generated/iphone-step-2.png', icon: BatteryCharging },
  { title: 'Тестування та калібрування', desc: 'Перевіряємо ємність АКБ, тачскрін, Face ID / True Tone, датчики. Показуємо результат перед видачею.', img: '/images/generated/iphone-step-3.png', icon: ShieldCheck }
];

const FAQS = [
  { q: 'Скільки триває заміна АКБ або дисплея?', a: 'Заміна акумулятора — 30–60 хвилин, заміна дисплея — 40–90 хвилин. Здебільшого робимо при вас.' },
  { q: 'Ви ставите оригінальні запчастини?', a: 'Так. Ми пропонуємо оригінальні компоненти або сертифіковані аналоги преміум-якості (копія). Ви самі обираєте варіант — обидві ціни озвучуємо заздалегідь.' },
  { q: 'Чи збережеться Face ID і True Tone після заміни дисплея?', a: 'Так. Ми переносимо рідні компоненти та програмуємо дисплей на спеціальному обладнанні, щоб True Tone і сенсори працювали коректно.' },
  { q: 'Яка гарантія на ремонт?', a: 'На заміну акумулятора — 90 днів, на заміну дисплея — 30 днів. Гарантія офіційна, з документом.' },
  { q: 'Дані з телефона нікуди не зникнуть?', a: 'Ні. Ремонт АКБ і дисплея не торкається пам\'яті та даних. Все залишається на місці.' }
];

const IphoneRepairPage = () => {
  const [active, setActive] = useState('13');
  const [tab, setTab] = useState('battery');
  const [apple, setApple] = useState(MOCK_APPLE);
  const landing = useLandingContent('phones');
  const g = GROUPS.find((x) => x.key === active);

  useEffect(() => {
    let ignore = false;
    fetch(`${BACKEND}/api/calculator-data/phone`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!ignore && data && data.Apple && data.Apple.models) setApple(data.Apple);
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    const description = 'Заміна акумулятора та дисплея iPhone в Одесі (11–15 Pro Max). Оригінальні запчастини, сертифіковані майстри, гарантія 90 днів на АКБ і 30 днів на дисплей. Заміна АКБ від 700 грн, дисплея від 1700 грн. ☎ +38 097 417 8788';
    document.title = 'Ремонт iPhone в Одесі — заміна АКБ та дисплея | CIMM Computers';

    const descriptionTag = document.head.querySelector('meta[name="description"]');
    let canonicalTag = document.head.querySelector('link[rel="canonical"]');

    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }

    if (descriptionTag) descriptionTag.setAttribute('content', description);
    canonicalTag.setAttribute('href', 'https://cimm.od.ua/remont-iphone');
  }, []);

  return (
    <div className="min-h-screen console-page" data-testid="iphone-repair-page">
      <Helmet>
        <meta name="keywords" content="ремонт iPhone Одеса, заміна акумулятора iPhone Одеса, заміна АКБ айфон Одеса, заміна дисплея iPhone Одеса, заміна екрану айфон, ремонт айфона Одеса" />
        <meta property="og:title" content="Ремонт iPhone в Одесі — заміна АКБ та дисплея | CIMM Computers" />
        <meta property="og:description" content="Оригінальні запчастини, сертифіковані майстри, гарантія до 90 днів. Заміна АКБ від 700 грн." />
        <meta property="og:image" content="https://cimm.od.ua/images/generated/iphone-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cimm.od.ua/remont-iphone" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Ремонт iPhone — заміна акумулятора та дисплея',
          provider: {
            '@type': 'LocalBusiness',
            name: 'CIMM Computers',
            telephone: '+380974178788',
            address: { '@type': 'PostalAddress', addressLocality: 'Одеса', addressCountry: 'UA' }
          },
          areaServed: { '@type': 'City', name: 'Одеса' },
          offers: [
            { '@type': 'Offer', name: 'Заміна акумулятора iPhone', price: '700', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Заміна дисплея iPhone', price: '1700', priceCurrency: 'UAH' }
          ]
        })}</script>
      </Helmet>
      <LandingFaqSchema category="iphone" />

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-teal-400 transition-all">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-teal-400 transition-all">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-teal-400 transition-all">Ноутбуки</Link>
            <Link to="/chistka-macbook" className="text-gray-300 hover:text-teal-400 transition-all">MacBook</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-teal-400 transition-all">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold" data-testid="header-phone-link">+38 097 417 8788</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden pt-28 pb-16 md:pt-24 md:pb-20 console-hero">
        <div className="absolute inset-0 pointer-events-none console-hero-bg" aria-hidden="true">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 90% at 75% 50%, rgba(45,212,191,0.2), transparent 65%)' }} />
        </div>

        <div className="absolute inset-y-0 right-0 w-full md:w-[58%] lg:w-[54%] pointer-events-none overflow-hidden">
          <img src="/images/generated/iphone-hero.png" alt="Ремонт iPhone" className="w-full h-full object-contain object-right" data-testid="iphone-hero-img" />
          <div className="absolute inset-y-0 left-0 w-1/2 console-hero-fade" />
        </div>

        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          <div className="flex flex-col items-start mb-8">
            <Link to="/" className="inline-flex items-center gap-2 hero-back-link mb-6 transition-colors text-sm" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> На головну
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/30 mb-5 backdrop-blur-sm">
              <Smartphone className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-xs uppercase tracking-widest text-teal-400">Ремонт iPhone · Одеса</span>
            </div>
          </div>

          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mb-5 hero-h1">
              Ремонт <span className={GRAD}>iPhone</span>
              <br />в Одесі
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-xl hero-lead">
              Заміна <span className="font-bold hero-lead-strong">акумулятора</span> та <span className="font-bold hero-lead-strong">дисплея</span> будь-якого iPhone. Оригінальні запчастини, ремонт при вас, гарантія до <span className="font-bold hero-lead-strong">90 днів</span>.
            </p>

            {/* Model switcher */}
            <div className="mb-5">
              <div className="text-xs uppercase tracking-widest hero-price-from mb-2">Оберіть модель</div>
              <div className="inline-flex flex-wrap gap-2 p-1.5 rounded-2xl console-switcher-bg backdrop-blur-xl border shadow-2xl" data-testid="iphone-switcher">
                {GROUPS.map((x) => (
                  <button
                    key={x.key}
                    onClick={() => setActive(x.key)}
                    data-testid={`switcher-${x.key}`}
                    className={`px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition-all duration-300 ${
                      active === x.key ? `bg-gradient-to-r ${ACCENT} text-white shadow-lg` : 'hero-switcher-idle'
                    }`}
                  >
                    {x.label.replace('iPhone ', '')}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="inline-flex p-1.5 rounded-full console-switcher-bg backdrop-blur-xl border mb-6" data-testid="iphone-tabs">
              <button onClick={() => setTab('battery')} data-testid="tab-battery" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === 'battery' ? `bg-gradient-to-r ${ACCENT} text-white` : 'hero-switcher-idle'}`}>
                <BatteryCharging className="w-4 h-4" /> Заміна АКБ
              </button>
              <button onClick={() => setTab('display')} data-testid="tab-display" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === 'display' ? `bg-gradient-to-r ${ACCENT} text-white` : 'hero-switcher-idle'}`}>
                <Smartphone className="w-4 h-4" /> Заміна дисплея
              </button>
            </div>

            {/* Price */}
            <div className="mb-8" data-testid="iphone-price-block">
              {tab === 'battery' ? (
                <div className="flex flex-wrap items-baseline gap-3">
                  <div className="hero-price-from text-sm">від</div>
                  <div className={`text-6xl md:text-7xl font-black ${GRAD}`}>{g.battery}</div>
                  <div className="hero-price-unit text-2xl font-bold">грн</div>
                  <div className="text-sm hero-meta ml-2">· {g.label} · гарантія 90 днів</div>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-6">
                  <div>
                    <div className="hero-price-from text-xs uppercase tracking-widest mb-1">Копія (аналог)</div>
                    <div className="flex items-baseline gap-2">
                      <span className="hero-price-from text-sm">від</span>
                      <span className={`text-5xl md:text-6xl font-black ${GRAD}`}>{g.displayCopy}</span>
                      <span className="hero-price-unit text-xl font-bold">грн</span>
                    </div>
                  </div>
                  <div>
                    <div className="hero-price-from text-xs uppercase tracking-widest mb-1">Оригінал</div>
                    <div className="flex items-baseline gap-2">
                      <span className="hero-price-from text-sm">від</span>
                      <span className="text-4xl md:text-5xl font-black text-white">{g.displayOriginal}</span>
                      <span className="hero-price-unit text-lg font-bold">грн</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <a href="tel:+380974178788" data-testid="hero-cta-call" className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 ${GLOW}`}>
                <Phone className="w-5 h-5" /> Записатись зараз
              </a>
              <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="hero-cta-telegram" className="inline-flex items-center gap-2 px-8 py-4 bg-[#229ED9] hover:bg-[#1c8dc4] text-white font-bold rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_40px_rgba(34,158,217,0.4)]">
                <Send className="w-5 h-5" /> Ціна в Telegram
              </a>
              <a href="#prices" data-testid="hero-cta-details" className="inline-flex items-center gap-2 px-8 py-4 hero-cta-secondary font-semibold rounded-full border transition-all">
                Калькулятор <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-8 text-sm hero-meta">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-teal-400" /> Ремонт при вас</div>
              <div className="flex items-center gap-2"><Cpu className="w-4 h-4 text-teal-400" /> Оригінальні запчастини</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-400" /> Гарантія до 90 днів</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust block */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-teal-400 mb-2">Чому нам довіряють</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Ремонт iPhone <span className={GRAD}>без ризику</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TRUST.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="glass-card p-8 text-center" data-testid={`trust-${i}`}>
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${ACCENT} flex items-center justify-center`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-black text-white mb-2">{t.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="prices" className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 mb-2"><Calculator className="w-4 h-4" /> Калькулятор</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Розрахуйте ціну <span className={GRAD}>ремонту</span></h2>
            <p className="text-gray-400 mt-3">Оберіть точну модель iPhone і послугу — покажемо орієнтовну ціну оригінал / копія.</p>
          </div>
          <IphoneCalculator apple={apple} />
          <p className="text-gray-500 text-xs mt-4 text-center">Гарантія: 90 днів на заміну АКБ · 30 днів на заміну дисплея. Немає вашої моделі — телефонуйте, порахуємо.</p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-teal-400 mb-2">Процес</div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Як проходить <span className={GRAD}>ремонт</span></h2>
          </div>
          <div className="space-y-8 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={i} className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`} data-testid={`step-${i}`}>
                  <div className="md:[direction:ltr]">
                    <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3] bg-black/40">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-teal-400 font-black text-lg border border-teal-500/40">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${ACCENT}`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-semibold">Крок {i + 1}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{s.title}</h3>
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <InstagramVideoSection
        url={landing?.instagram_video}
        accentClass="text-teal-400"
      />

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-teal-400 mb-2">FAQ</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Часті питання</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-card p-6 group cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-white font-semibold list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-teal-400 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="glass-card p-8 md:p-14 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Поверніть iPhone до <span className={GRAD}>життя</span></h2>
              <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">Тримає заряд півдня? Розбитий екран? Один дзвінок — і сьогодні ж заберете справний iPhone з гарантією.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="tel:+380974178788" data-testid="final-cta-call" className={`inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full text-lg ${GLOW}`}>
                  <Phone className="w-5 h-5" /> +38 097 417 8788
                </a>
                <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="final-cta-telegram" className="inline-flex items-center gap-2 px-10 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
                  <Send className="w-5 h-5" /> Написати в Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IphoneRepairPage;
